import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../API';
import CreateFolderModal from './CreateFolderModal';
import FolderAccessModal from './FolderAccessModal';
import ProjectsPageShell from './hub/ProjectsPageShell';
import ProjectBrowseKindIcon from './hub/ProjectBrowseKindIcon';
import { IconBranding, IconExternal, IconPencil, IconSettings, IconSharing } from './hub/HubRowActionIcons';
import OrganizationBrandingModal from './OrganizationBrandingModal';
import HubRowPrimaryTrailIcon from './hub/HubRowPrimaryTrailIcon';
import HubProjectStatusChip from './hub/HubProjectStatusChip';
import RenameResourceModal from './hub/RenameResourceModal';
import styles from './ProjectsHome.module.css';

/** Girder AccessType — WRITE (1) to rename; ADMIN (2) to view/edit sharing. */
const GIRDER_ACCESS_WRITE = 1;
const GIRDER_ACCESS_ADMIN = 2;

const TYPE_LABELS = {
    organization: 'Folder',
    project: 'Project',
    'project-subfolder': 'Subfolder',
    slideset: 'Slide Set',
    'slideset-subfolder': 'Subfolder',
};

/** Stable browse order: container folders first, then subfolders, then projects/slidesets; then by name. */
const BROWSE_TYPE_ORDER = {
    organization: 0,
    'project-subfolder': 1,
    'slideset-subfolder': 1,
    project: 2,
    slideset: 2,
};

function compareBrowseFolders(a, b) {
    const ta = a.girderProjects?.type ?? '';
    const tb = b.girderProjects?.type ?? '';
    const oa = BROWSE_TYPE_ORDER[ta] ?? 99;
    const ob = BROWSE_TYPE_ORDER[tb] ?? 99;
    if (oa !== ob) return oa - ob;
    return String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' });
}

function isLeafContainerType(type) {
    return type === 'project' || type === 'slideset';
}

/** Split root browse entries into folder-like vs project/slideset rows; sort each bucket. */
function splitAndSortBrowseRoot(rootFolders, rootAccessPoints) {
    const folderLike = [];
    const projectLike = [];
    for (const folder of rootFolders || []) {
        const entry = { folder, accessPoint: false };
        if (isLeafContainerType(folder.girderProjects?.type)) {
            projectLike.push(entry);
        } else {
            folderLike.push(entry);
        }
    }
    for (const folder of rootAccessPoints || []) {
        const entry = { folder, accessPoint: true };
        if (isLeafContainerType(folder.girderProjects?.type)) {
            projectLike.push(entry);
        } else {
            folderLike.push(entry);
        }
    }
    folderLike.sort((a, b) => compareBrowseFolders(a.folder, b.folder));
    projectLike.sort((a, b) => compareBrowseFolders(a.folder, b.folder));
    return { folderLike, projectLike };
}

/** Drill-down children: same partition; all accessPoint false. */
function splitAndSortBrowseDrillDown(folders) {
    const folderLike = [];
    const projectLike = [];
    for (const folder of folders || []) {
        const entry = { folder, accessPoint: false };
        if (isLeafContainerType(folder.girderProjects?.type)) {
            projectLike.push(entry);
        } else {
            folderLike.push(entry);
        }
    }
    folderLike.sort((a, b) => compareBrowseFolders(a.folder, b.folder));
    projectLike.sort((a, b) => compareBrowseFolders(a.folder, b.folder));
    return { folderLike, projectLike };
}

function BrowseKindMarker({ type }) {
    const label = TYPE_LABELS[type] || type;
    const isLeaf = isLeafContainerType(type);
    return (
        <span
            className={`${styles.browseKind} ${isLeaf ? styles.browseKindProject : styles.browseKindFolder}`}
            title={label}
        >
            <ProjectBrowseKindIcon type={type} />
            <span className={styles.visuallyHidden}>{label}</span>
        </span>
    );
}

/**
 * ProjectsManage — browse folder hierarchy (Girder projects collection).
 * /projects/manage: root children + access points
 * /projects/manage/:folderId: drill-down
 */
function ProjectsManage() {
    const { folderId } = useParams();
    const navigate = useNavigate();

    const [rootData, setRootData] = useState(null);
    const [collection, setCollection] = useState(null);
    const [currentFolder, setCurrentFolder] = useState(null);
    const [ancestors, setAncestors] = useState([]);
    const [folders, setFolders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createMode, setCreateMode] = useState('auto');
    const [accessFolder, setAccessFolder] = useState(null);
    const [renameTarget, setRenameTarget] = useState(null);
    const [brandingTarget, setBrandingTarget] = useState(null);
    const renameTargetRef = useRef(null);

    useEffect(() => {
        renameTargetRef.current = renameTarget;
    }, [renameTarget]);

    useEffect(() => {
        if (folderId) return;
        setLoading(true);
        setError(null);
        api.get('projects/root', {})
            .then((r) => {
                if (r.status === 200) {
                    setRootData(r.data);
                } else {
                    setError(r.data?.message || 'Failed to load.');
                }
            })
            .catch((e) => {
                setError(e.message || 'Failed to load.');
                setRootData(null);
            })
            .finally(() => setLoading(false));
    }, [folderId]);

    useEffect(() => {
        if (!folderId) return;
        setLoading(true);
        setError(null);
        setAncestors([]);
        setCurrentFolder(null);

        const foldersPromise = api.get('projects/folders', { query: { parentId: folderId } });
        const rootpathPromise = api.get(`folder/${folderId}/rootpath`, {});

        Promise.all([foldersPromise, rootpathPromise])
            .then(([foldersRes, rootpathRes]) => {
                if (foldersRes.status !== 200) {
                    setError(foldersRes.data?.message || 'Failed to load folders.');
                    return;
                }
                const { collection: col, currentFolder: cur, folders: kids } = foldersRes.data;
                setCollection(col);
                setCurrentFolder(cur ?? null);
                setFolders(kids || []);

                if (rootpathRes?.status === 200 && Array.isArray(rootpathRes.data)) {
                    const path = rootpathRes.data;
                    const projectFolderAncestors = path
                        .filter((entry) => entry.type === 'folder' && entry.object?.girderProjects)
                        .map((entry) => entry.object)
                        .filter((f) => String(f._id) !== String(folderId));
                    setAncestors(projectFolderAncestors);
                }
            })
            .catch((e) => setError(e.message || 'Failed to load folders.'))
            .finally(() => setLoading(false));
    }, [folderId]);

    function handleFolderCreated(newFolder) {
        setFolders((prev) => [...prev, newFolder]);
    }

    function handleRenameComplete(target, data) {
        if (!target || !data) return;
        if (target.resourceType === 'collection') {
            setCollection((c) => (c ? { ...c, ...data } : c));
            setRootData((prev) => {
                if (!prev?.collection || String(prev.collection._id) !== String(target.resourceId)) {
                    return prev;
                }
                return { ...prev, collection: { ...prev.collection, ...data } };
            });
            return;
        }
        const sid = String(target.resourceId);
        const newName = data.name;
        setRootData((prev) => {
            if (!prev) return prev;
            const map = (arr) =>
                (arr || []).map((f) =>
                    String(f._id) === sid ? { ...f, ...data, name: newName } : f,
                );
            return {
                ...prev,
                folders: map(prev.folders),
                accessPoints: map(prev.accessPoints),
            };
        });
        setFolders((prev) =>
            prev.map((f) => (String(f._id) === sid ? { ...f, ...data, name: newName } : f)),
        );
        setAncestors((prev) =>
            prev.map((f) => (String(f._id) === sid ? { ...f, ...data, name: newName } : f)),
        );
        setCurrentFolder((cf) =>
            cf && String(cf._id) === sid ? { ...cf, ...data, name: newName } : cf,
        );
        setAccessFolder((af) => (af && String(af.id) === sid ? { ...af, name: newName } : af));
    }

    function handleBrandingSuccess(updatedFolder) {
        const apply = (f) => {
            if (!f || String(f._id) !== String(updatedFolder?._id)) return f;
            return {
                ...f,
                girderProjects: {
                    ...(f.girderProjects || {}),
                    branding: updatedFolder.girderProjects?.branding,
                },
            };
        };
        setRootData((prev) => (prev ? {
            ...prev,
            folders: (prev.folders || []).map(apply),
            accessPoints: (prev.accessPoints || []).map(apply),
        } : prev));
        setFolders((prev) => (prev || []).map(apply));
        setCurrentFolder((prev) => apply(prev));
        setBrandingTarget(null);
    }

    function renderBrandingModal() {
        return (
            <OrganizationBrandingModal
                open={!!brandingTarget}
                folderId={brandingTarget?.folderId ?? null}
                folderName={brandingTarget?.folderName ?? ''}
                initialTitle={brandingTarget?.title ?? ''}
                initialLogoItemId={brandingTarget?.logoItemId ?? ''}
                onClose={() => setBrandingTarget(null)}
                onSuccess={handleBrandingSuccess}
            />
        );
    }

    const currentFolderType = currentFolder?.girderProjects?.type ?? null;
    const isOrgFolder = currentFolderType === 'organization';
    const isProjectLikeFolder = currentFolderType === 'project' || currentFolderType === 'project-subfolder';
    const isSlideSetLikeFolder = currentFolderType === 'slideset' || currentFolderType === 'slideset-subfolder';
    const parentId = currentFolder?._id ?? (collection?._id ?? folderId);
    const parentType = currentFolder ? 'folder' : 'collection';

    function openBrowseRow(folder) {
        const ftype = folder.girderProjects?.type;
        if (ftype === 'project') {
            navigate(`/projects/${folder._id}/participate`);
        } else if (ftype === 'slideset') {
            navigate(`/slidesets/${folder._id}`);
        } else {
            navigate(`/projects/manage/${folder._id}`);
        }
    }

    function renderBrowseRow(folder, { accessPoint = false } = {}) {
        const ftype = folder.girderProjects?.type;
        const isProject = ftype === 'project';
        const isSlideSet = ftype === 'slideset';
        const isLeaf = isProject || isSlideSet;
        const showAccessPointOrg =
            accessPoint && ftype === 'organization';
        const laneClass = isLeaf ? styles.hubRowProject : styles.hubRowFolder;
        const rowClass = [styles.hubRow, laneClass, showAccessPointOrg ? styles.folderRowAccessPoint : '']
            .filter(Boolean)
            .join(' ');
        const isProjectAdmin = isProject && (folder.myRoles || []).includes('admin');
        const canRenameRow = isProject
            ? isProjectAdmin
            : typeof folder.myAccess === 'number' && folder.myAccess >= GIRDER_ACCESS_WRITE;
        const canShareRow = typeof folder.myAccess === 'number' && folder.myAccess >= GIRDER_ACCESS_ADMIN;
        const canBrandOrg = ftype === 'organization'
            && typeof folder.myAccess === 'number'
            && folder.myAccess >= GIRDER_ACCESS_WRITE;
        const canSettingsSlideSet = isSlideSet
            && typeof folder.myAccess === 'number'
            && folder.myAccess >= GIRDER_ACCESS_WRITE;
        const primaryLabel = isProject
            ? `Open project workspace: ${folder.name}`
            : (isSlideSet
                ? `Open Slide Set: ${folder.name}`
                : `Expand into this folder in the browse tree: ${folder.name}`);
        const primaryTitle = isProject
            ? `Open project workspace — ${folder.name}`
            : (isSlideSet
                ? `Open Slide Set landing — ${folder.name}`
                : `Expand into this folder in the browse tree — ${folder.name}`);
        const trailVariant = isLeaf ? 'project' : 'folder';
        return (
            <li key={`${accessPoint ? 'ap-' : ''}${folder._id}`} className={rowClass}>
                <div
                    role="button"
                    tabIndex={0}
                    className={`${styles.hubRowPrimary} ${isLeaf ? styles.hubRowPrimaryProject : styles.hubRowPrimaryFolder}`}
                    onClick={() => openBrowseRow(folder)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            openBrowseRow(folder);
                        }
                    }}
                    aria-label={primaryLabel}
                    title={primaryTitle}
                >
                    <div className={styles.hubRowMain}>
                        <div className={styles.hubRowTitleRow}>
                            {showAccessPointOrg ? (
                                <span className={styles.accessPointBadge}>Access point</span>
                            ) : null}
                            <BrowseKindMarker type={ftype} />
                            <span className={styles.hubRowName}>{folder.name}</span>
                            {isProject ? (
                                <HubProjectStatusChip lifecycleStatus={folder.girderProjects?.lifecycleStatus} />
                            ) : null}
                            {isSlideSet && folder.public ? (
                                <span className={styles.accessPointBadge} title="Public Slide Set">Public</span>
                            ) : null}
                            {canRenameRow ? (
                                <button
                                    type="button"
                                    className={styles.hubRowRenameBtn}
                                    title="Rename"
                                    aria-label={`Rename ${folder.name}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setRenameTarget({
                                            folderId: folder._id,
                                            initialName: folder.name,
                                        });
                                    }}
                                >
                                    <IconPencil />
                                </button>
                            ) : null}
                        </div>
                    </div>
                    <span className={styles.hubRowPrimaryTrail}>
                        <HubRowPrimaryTrailIcon variant={trailVariant} />
                    </span>
                </div>
                <div className={styles.hubRowActions}>
                    {canBrandOrg ? (
                        <button
                            type="button"
                            className={styles.hubIconBtn}
                            title="Branding — title and logo for participant chrome"
                            aria-label={`Branding for ${folder.name}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                const branding = folder.girderProjects?.branding || {};
                                setBrandingTarget({
                                    folderId: folder._id,
                                    folderName: folder.name,
                                    title: branding.title || '',
                                    logoItemId: branding.logoItemId || '',
                                });
                            }}
                        >
                            <IconBranding />
                        </button>
                    ) : null}
                    {isProjectAdmin ? (
                        <button
                            type="button"
                            className={styles.hubIconBtn}
                            title="Project settings — annotation UI, participants, items"
                            aria-label="Project settings"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/projects/${folder._id}`);
                            }}
                        >
                            <IconSettings />
                        </button>
                    ) : null}
                    {canSettingsSlideSet ? (
                        <button
                            type="button"
                            className={styles.hubIconBtn}
                            title="Slide Set settings — items, metadata, sharing"
                            aria-label="Slide Set settings"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/projects/${folder._id}`);
                            }}
                        >
                            <IconSettings />
                        </button>
                    ) : null}
                    {canShareRow ? (
                        <button
                            type="button"
                            className={styles.hubIconBtn}
                            title="Sharing — who can access this folder or project"
                            aria-label="Sharing"
                            onClick={() => setAccessFolder({
                                id: folder._id,
                                name: folder.name,
                                folderType: ftype,
                            })}
                        >
                            <IconSharing />
                        </button>
                    ) : null}
                    <a
                        href={`/dsa#folder/${folder._id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.hubIconLink}
                        title="Open in Girder — default DSA / Girder folder UI"
                        aria-label="Open in Girder"
                    >
                        <IconExternal />
                    </a>
                </div>
            </li>
        );
    }

    function renderBrowseSections(folderLike, projectLike, idPrefix) {
        if (folderLike.length === 0 && projectLike.length === 0) {
            return null;
        }
        return (
            <div className={styles.browseSections}>
                {folderLike.length > 0 ? (
                    <section className={styles.browseSection} aria-labelledby={`${idPrefix}-folders-h`}>
                        <h2 id={`${idPrefix}-folders-h`} className={styles.browseSectionHeading}>
                            Folders
                        </h2>
                        <p className={styles.browseSectionHint}>
                            Click a row to open the folder.
                        </p>
                        <ul className={styles.hubList}>
                            {folderLike.map(({ folder, accessPoint }) =>
                                renderBrowseRow(folder, { accessPoint }),
                            )}
                        </ul>
                    </section>
                ) : null}
                {projectLike.length > 0 ? (
                    <section className={styles.browseSection} aria-labelledby={`${idPrefix}-projects-h`}>
                        <h2 id={`${idPrefix}-projects-h`} className={styles.browseSectionHeading}>
                            Projects &amp; Slide Sets
                        </h2>
                        <p className={styles.browseSectionHint}>
                            Click a project to open its workspace, or a Slide Set to open its landing page.
                            Use the gear icon to configure.
                        </p>
                        <ul className={styles.hubList}>
                            {projectLike.map(({ folder, accessPoint }) =>
                                renderBrowseRow(folder, { accessPoint }),
                            )}
                        </ul>
                    </section>
                ) : null}
            </div>
        );
    }

    // ── Root view (no folderId) ─────────────────────────────────────────────
    if (!folderId) {
        const rootFolders = rootData?.folders ?? [];
        const rootAccessPoints = rootData?.accessPoints ?? [];
        const canCreateAtRoot = rootData?.canCreateAtRoot === true;
        const rootCollection = rootData?.collection;
        const rootEmpty = rootFolders.length === 0 && rootAccessPoints.length === 0;
        const hasOrgAccessPoints = rootAccessPoints.some(
            (f) => f.girderProjects?.type === 'organization',
        );
        const accessPointCallout = hasOrgAccessPoints ? (
            <>
                Some <strong>folders</strong> are <strong>access points</strong>: you were granted access
                here without access to the parent folder. You can still open them and manage sharing
                where allowed.
            </>
        ) : null;

        const { folderLike: rootFolderLike, projectLike: rootProjectLike } = splitAndSortBrowseRoot(
            rootFolders,
            rootAccessPoints,
        );

        return (
            <ProjectsPageShell
                title="Browse and organize"
                callout={accessPointCallout}
                loading={loading}
                error={error}
                headerActions={
                    !loading && !error && canCreateAtRoot && rootCollection ? (
                        <>
                            <button
                                type="button"
                                className={styles.buttonPrimary}
                                onClick={() => {
                                    setCollection(rootCollection);
                                    setCurrentFolder(null);
                                    setFolders(rootFolders);
                                    setCreateMode('project');
                                    setShowCreateModal(true);
                                }}
                            >
                                + New project
                            </button>
                            <button
                                type="button"
                                className={styles.buttonPrimary}
                                onClick={() => {
                                    setCollection(rootCollection);
                                    setCurrentFolder(null);
                                    setFolders(rootFolders);
                                    setCreateMode('slideset');
                                    setShowCreateModal(true);
                                }}
                            >
                                + New Slide Set
                            </button>
                            <button
                                type="button"
                                className={styles.buttonSecondary}
                                onClick={() => {
                                    setCollection(rootCollection);
                                    setCurrentFolder(null);
                                    setFolders(rootFolders);
                                    setCreateMode('folder');
                                    setShowCreateModal(true);
                                }}
                            >
                                + New folder
                            </button>
                        </>
                    ) : null
                }
            >
                {rootEmpty ? (
                    <p className={styles.emptyStatePlain}>
                        You do not have access to any folders here yet. Ask an administrator to grant
                        access, or use <strong>My projects</strong> if you were assigned to a project
                        directly.
                    </p>
                ) : (
                    renderBrowseSections(rootFolderLike, rootProjectLike, 'browse-root')
                )}
                {showCreateModal && rootCollection && (
                    <CreateFolderModal
                        isOpen={showCreateModal}
                        onClose={() => setShowCreateModal(false)}
                        parentId={rootCollection._id}
                        parentType="collection"
                        parentFolderType={null}
                        mode={createMode}
                        onCreated={(newFolder) => {
                            setRootData((prev) => (prev
                                ? {
                                    ...prev,
                                    folders: [...(prev.folders || []), newFolder],
                                }
                                : prev));
                            setShowCreateModal(false);
                        }}
                    />
                )}
                {accessFolder && (
                    <FolderAccessModal
                        isOpen={!!accessFolder}
                        onClose={() => setAccessFolder(null)}
                        folderId={accessFolder.id}
                        folderName={accessFolder.name}
                        folderType={accessFolder.folderType}
                        allowPublicToggle={accessFolder.folderType === 'slideset'}
                    />
                )}
                <RenameResourceModal
                    open={!!renameTarget}
                    folderId={renameTarget?.folderId ?? null}
                    initialName={renameTarget?.initialName ?? ''}
                    onClose={() => setRenameTarget(null)}
                    onSuccess={(data) => handleRenameComplete(renameTargetRef.current, data)}
                />
                {renderBrandingModal()}
            </ProjectsPageShell>
        );
    }

    // ── Drill-down ────────────────────────────────────────────────────────
    if (loading) {
        return <ProjectsPageShell title="Browse and organize" loading />;
    }

    if (error) {
        return <ProjectsPageShell title="Browse and organize" error={error} />;
    }

    if (!collection) {
        return (
            <ProjectsPageShell
                title="Browse and organize"
                error="Could not load this folder. It may not exist or you may not have access."
            />
        );
    }

    const headerActionsDrill = (
        <>
            {currentFolder
                && typeof currentFolder.myAccess === 'number'
                && currentFolder.myAccess >= GIRDER_ACCESS_ADMIN ? (
                <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => setAccessFolder({
                        id: currentFolder._id,
                        name: currentFolder.name,
                        folderType: currentFolderType,
                    })}
                >
                    Sharing for this folder
                </button>
            ) : null}
            {isOrgFolder
                && typeof currentFolder?.myAccess === 'number'
                && currentFolder.myAccess >= GIRDER_ACCESS_WRITE ? (
                <button
                    type="button"
                    className={styles.linkButton}
                    onClick={() => {
                        const branding = currentFolder.girderProjects?.branding || {};
                        setBrandingTarget({
                            folderId: currentFolder._id,
                            folderName: currentFolder.name,
                            title: branding.title || '',
                            logoItemId: branding.logoItemId || '',
                        });
                    }}
                >
                    Branding
                </button>
            ) : null}
            {isOrgFolder ? (
                <>
                    <button
                        type="button"
                        className={styles.buttonPrimary}
                        onClick={() => {
                            setCreateMode('project');
                            setShowCreateModal(true);
                        }}
                    >
                        + New project
                    </button>
                    <button
                        type="button"
                        className={styles.buttonPrimary}
                        onClick={() => {
                            setCreateMode('slideset');
                            setShowCreateModal(true);
                        }}
                    >
                        + New Slide Set
                    </button>
                    <button
                        type="button"
                        className={styles.buttonSecondary}
                        onClick={() => {
                            setCreateMode('folder');
                            setShowCreateModal(true);
                        }}
                    >
                        + New folder
                    </button>
                </>
            ) : null}
            {isProjectLikeFolder || isSlideSetLikeFolder ? (
                <button
                    type="button"
                    className={styles.buttonPrimary}
                    onClick={() => {
                        setCreateMode('subfolder');
                        setShowCreateModal(true);
                    }}
                >
                    + New subfolder
                </button>
            ) : null}
        </>
    );

    const { folderLike: drillFolderLike, projectLike: drillProjectLike } =
        splitAndSortBrowseDrillDown(folders);

    return (
        <ProjectsPageShell title="Browse and organize" headerActions={headerActionsDrill}>
            <nav className={styles.breadcrumb} aria-label="Folder path from projects root">
                <button
                    type="button"
                    className={styles.breadcrumbLink}
                    onClick={() => navigate('/projects/manage')}
                >
                    Projects root
                </button>
                {ancestors.map((f) => (
                    <span key={f._id} style={{ display: 'contents' }}>
                        <span className={styles.breadcrumbSeparator}>/</span>
                        <button
                            type="button"
                            className={styles.breadcrumbLink}
                            onClick={() => navigate(`/projects/manage/${f._id}`)}
                        >
                            {f.name}
                        </button>
                    </span>
                ))}
                {currentFolder ? (
                    <span style={{ display: 'contents' }}>
                        <span className={styles.breadcrumbSeparator}>/</span>
                        <span className={styles.breadcrumbRenameCluster}>
                            <span className={styles.breadcrumbCurrent}>{currentFolder.name}</span>
                            {typeof currentFolder.myAccess === 'number' &&
                            currentFolder.myAccess >= GIRDER_ACCESS_WRITE ? (
                                <button
                                    type="button"
                                    className={styles.breadcrumbRenameBtn}
                                    title="Rename folder"
                                    aria-label={`Rename ${currentFolder.name}`}
                                    onClick={() =>
                                        setRenameTarget({
                                            folderId: currentFolder._id,
                                            initialName: currentFolder.name,
                                        })
                                    }
                                >
                                    <IconPencil />
                                </button>
                            ) : null}
                        </span>
                    </span>
                ) : (
                    <span style={{ display: 'contents' }}>
                        <span className={styles.breadcrumbSeparator}>/</span>
                        <span className={styles.breadcrumbCurrent}>{collection.name}</span>
                    </span>
                )}
            </nav>

            {folders.length === 0 ? (
                <p className={styles.emptyStatePlain}>No subfolders or projects here yet.</p>
            ) : (
                renderBrowseSections(drillFolderLike, drillProjectLike, `browse-${folderId}`)
            )}

            {accessFolder && (
                <FolderAccessModal
                    isOpen={!!accessFolder}
                    onClose={() => setAccessFolder(null)}
                    folderId={accessFolder.id}
                    folderName={accessFolder.name}
                    folderType={accessFolder.folderType}
                    allowPublicToggle={accessFolder.folderType === 'slideset'}
                />
            )}

            {showCreateModal && (
                <CreateFolderModal
                    isOpen={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    parentId={parentId}
                    parentType={parentType}
                    parentFolderType={currentFolderType}
                    mode={createMode}
                    onCreated={handleFolderCreated}
                />
            )}
            <RenameResourceModal
                open={!!renameTarget}
                folderId={renameTarget?.folderId ?? null}
                initialName={renameTarget?.initialName ?? ''}
                onClose={() => setRenameTarget(null)}
                onSuccess={(data) => handleRenameComplete(renameTargetRef.current, data)}
            />
            {renderBrandingModal()}
        </ProjectsPageShell>
    );
}

export default ProjectsManage;
